import React, { useEffect, useRef, useState } from "react";
import {
  ChatWindowComponentPaper,
  MessageContainer,
  ChatWindowAvatar,
} from "../../pages/Chatbox/styles/ChatWindowComponent.styled";
import { MessageWrapper } from "../../pages/Chatbox/styles/ChatWindowComponent.styled";
import ShineThinking from "../../elements/ShineThinking";
import { toJS } from "mobx";
import { useMediaQueries } from "../../utils/mediaQueries";
import AnnAvatarDark from '../../images/icon_dark_bg.png';
import HomeEnhancedMessage from './HomeEnhancedMessage';
import TextSelectionMenu from './components/TextSelectionMenu';
import DAGProgressIndicator from './components/DAGProgressIndicator';

/**
 * A modified ChatWindowComponent for the HomePage that uses HomeEnhancedMessage
 * to enable document editing directly from chat messages with home-specific styles
 */
const HomePageChatWindow = ({
  messages,
  bottomRef,
  className,
  displayButtons = true,
  width,
  height,
  chatWindowHeight,
  agentIsTyping,
  boxShadow,
  showImage,
  selectedAgent,
  dataTestId = "home-page-chat-window",
  onAddChatMessage, // Callback to add messages to chat
  onSendToReasoningAgent, // Callback to send selected text to reasoning agent
  onSendReasoningToRegularAgent, // Callback to send reasoning result to regular agent
  onEnterDocumentEditMode, // NEW: Callback to enter document edit mode
  onRegenerateMessage, // NEW: Callback to regenerate message
  regeneratingMessageIndex, // NEW: Index of message being regenerated
  onMarkWizardSubmitted, // NEW: Callback to mark wizard as submitted
  reasoningProgress,
  showAgentName = true, // NEW: Control whether to show agent name in messages
}) => {
  // Filter out consecutive messages of the same type, keeping only the last received message
  let filteredMessages = [];
  let lastReceivedIndex = -1;
  const chatWindowRef = useRef(null);
  const [agentImg, setAgentImg] = useState(AnnAvatarDark);
  const {isMobile, isDesktop, isIpadAirLandscape,isIpadAirPortrait, isIpadProLandscape, isIpadProPortrait} = useMediaQueries();

  useEffect(() => {
    const agent = toJS(selectedAgent)
    if (agent?.photo_url) {
      setAgentImg(agent.photo_url)
    }
  }, [selectedAgent])

  useEffect(() => {
    const handleLinkClick = (event) => {
      const link = event.target.closest("a");
      if (link && link.tagName === "A" && link.target !== "_blank") {
        event.preventDefault();
        window.open(link.href, "_blank");
        event.stopPropagation();
      }
    };

    const chatWindow = chatWindowRef.current;
    if (chatWindow) {
      chatWindow.addEventListener("click", handleLinkClick);
    }

    return () => {
      if (chatWindow) {
        chatWindow.removeEventListener("click", handleLinkClick);
      }
    };
  }, []);

  messages.forEach((message, index) => {
    // DAG progress messages and wizard messages should always be included and not filtered
    const isDagProgressMessage = message.isDagProgressMessage && message.dagProgress && message.dagProgress.length > 0;
    const isWizardMessage = message.wizardSubmitted === true; // Wizard with submitted answers
    
    if (isWizardMessage) {
      console.log('🎯 Preserving wizard message at index:', index, 'wizardSubmitted:', message.wizardSubmitted);
    }
    
    if (message.type !== "received" || isDagProgressMessage || isWizardMessage) {
      // Always include non-received messages, DAG progress messages, and wizard messages
      filteredMessages.push(message);
    } else {
      // Check if this received message is consecutive with the previous one
      // But skip DAG progress and wizard messages when checking for consecutive messages
      const prevMessage = index > 0 ? messages[index - 1] : null;
      const isPrevDagProgress = prevMessage?.isDagProgressMessage && prevMessage?.dagProgress && prevMessage?.dagProgress.length > 0;
      const isPrevWizard = prevMessage?.wizardSubmitted === true;
      
      if (index === 0 || prevMessage?.type !== "received" || isPrevDagProgress || isPrevWizard) {
        lastReceivedIndex = filteredMessages.length;
        filteredMessages.push(message);
      } else {
        filteredMessages[lastReceivedIndex] = message;
      }
    }
  });

  // Find the index of the last agent message in the original messages array
  const lastAgentMessageIndex = messages.map((m, i) => ({ ...m, originalIndex: i }))
    .reverse()
    .find(m => m.type === 'received')?.originalIndex;

  // Handler for text selection in chat
  const handleTextSelectionAction = (instruction, selectedText) => {
    console.log('📝 Chat text selection action:', { instruction, selectedText: selectedText.substring(0, 50) });
    if (onSendToReasoningAgent) {
      onSendToReasoningAgent(instruction, selectedText);
    }
  };

  // Render the chat window
  const renderChatWindow = () => (
    <ChatWindowComponentPaper
      ref={chatWindowRef}
      elevation={3}
      className={`${className} chat-window-component-paper`}
      $height={height}
      chatWindowHeight={chatWindowHeight}
      boxShadow={boxShadow}
      isMobile={isMobile}
      isDesktop={isDesktop}
      isIpadAirLandscape={isIpadAirLandscape}
      isIpadAirPortrait={isIpadAirPortrait}
      isIpadProLandscape={isIpadProLandscape}
      isIpadProPortrait={isIpadProPortrait}
      data-testid={dataTestId}
      style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
    >
      {/* Text selection menu for chat messages */}
      {onSendToReasoningAgent && (
        <TextSelectionMenu
          onSendToAgent={handleTextSelectionAction}
          containerRef={chatWindowRef}
        />
      )}

      {showImage && !messages.length && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <img src={agentImg} alt="No messages" />
        </div>
      )}

      {filteredMessages.map((item, index) => {
        // Generate a stable unique key for each message
        const messageKey = item.messageId || item.id || `msg-${index}-${item.time || ''}-${item.type || ''}`;
        
        // Check if this is a DAG progress message (persisted from previous reasoning)
        const isDagProgressMessage = item.isDagProgressMessage && item.dagProgress && item.dagProgress.length > 0;
        
        // Render DAG progress messages separately
        if (isDagProgressMessage) {
          return (
            <MessageContainer key={`dag-${messageKey}`} width={width}>
              <ChatWindowAvatar src={AnnAvatarDark} alt="Agent Avatar" />
              <MessageWrapper>
                <DAGProgressIndicator
                  dagProgress={item.dagProgress || []}
                  currentDagNode={null} // No current node for completed reasoning
                  isVisible
                  isComplete={true} // Always complete for persisted messages
                />
              </MessageWrapper>
            </MessageContainer>
          );
        }
        
        // Regular message rendering
        // Only show regenerate button on the last agent message
        const isLastAgentMessage = index === lastAgentMessageIndex;
        
        return (
          <HomeEnhancedMessage
            key={messageKey}
            messageIndex={index}
            messageItem={item}
            displayButtons={displayButtons}
            width={width}
            selectedAgent={selectedAgent}
            isTyping={item.isTyping}
            isSideBySideActive={false}
            isRegenerating={regeneratingMessageIndex === index}
            isLastAgentMessage={isLastAgentMessage}
            onAddChatMessage={onAddChatMessage}
            onSendToRegularAgent={onSendReasoningToRegularAgent}
            onEnterDocumentEditMode={onEnterDocumentEditMode}
            onRegenerateMessage={onRegenerateMessage}
            onMarkWizardSubmitted={onMarkWizardSubmitted}
            showAgentName={showAgentName}
          />
        );
      })}

      {/* Show DAG Progress Indicator ONLY when reasoning is actively running */}
      {reasoningProgress &&
        reasoningProgress.isActive && (
        <MessageContainer width={width}>
          <ChatWindowAvatar src={AnnAvatarDark} alt="Agent Avatar" />
          <MessageWrapper>
            <DAGProgressIndicator
              dagProgress={reasoningProgress.dagProgress || []}
              currentDagNode={reasoningProgress.currentDagNode}
              isVisible
              isComplete={false} // Active reasoning is not complete
            />
          </MessageWrapper>
        </MessageContainer>
      )}

      {/* Show thinking indicator for regular agents when typing (not reasoning) */}
      {agentIsTyping && 
       !reasoningProgress?.isActive && (
        <MessageContainer width={width}>
          <ChatWindowAvatar src={AnnAvatarDark} alt="Agent Avatar" />
          <MessageWrapper $flex data-testid="thinking-indicator">
            <ShineThinking />
          </MessageWrapper>
        </MessageContainer>
      )}

      <div ref={bottomRef} />
    </ChatWindowComponentPaper>
  );

  return renderChatWindow();
};

export default HomePageChatWindow; 